"use client"

import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { WatchlistStockTable, type ColKey } from "@/components/backtest/WatchlistStockTable"
import type { WatchlistFilterOptions } from "@/types/backtest"
import { RemoveFromAutoTradingListButton } from "@/components/trading/RemoveFromAutoTradingListButton"
import { getAutoTradingStocks, getAutoTradingStockSettings, updateAutoTradingStockSettings } from "@/lib/api/autotrading"
import { getPortfolioAccounts } from "@/lib/api/portfolio"
import { FieldRow, SaveButton, CardSkeleton, RADIO_CLASS, INPUT_CLASS } from "@/components/trading/shared"
import {
  STRATEGY_TYPE_LABELS,
  BUY_PRICE_TYPE_LABELS,
  COMPARISON_LABELS,
} from "@/types/autotrading"
import type {
  AutoTradingStockListItem,
  AutoTradingStockSetting,
  StrategyType,
  PositionMode,
  BuyPriceType,
  Comparison,
  QtyMode,
  SellTargetMode,
  SellQtyMode,
} from "@/types/autotrading"
import type { PortfolioAccount } from "@/types/portfolio"

function ToggleButtons<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  disabled?: boolean
}) {
  return (
    <div className="flex overflow-hidden rounded-md border border-zinc-700">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={cn(
            "px-2.5 py-1 text-xs transition-colors disabled:cursor-not-allowed",
            value === opt.value ? "bg-emerald-600 text-white" : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function AutoTradingStockSettingForm({
  stockName,
  setting,
  accounts,
}: {
  stockName: string
  setting: AutoTradingStockSetting
  accounts: PortfolioAccount[]
}) {
  const queryClient = useQueryClient()
  const stockCode = setting.stockCode

  const [isActive, setIsActive] = useState(setting.isActive)
  const [accountNumber, setAccountNumber] = useState(setting.accountNumber || accounts[0]?.accountNumber || "")
  const [strategyType, setStrategyType] = useState<StrategyType>(setting.strategyType)
  const [positionMode, setPositionMode] = useState<PositionMode>(setting.positionMode)
  const [positionValue, setPositionValue] = useState(String(setting.positionValue))
  const [enableBuy, setEnableBuy] = useState(setting.enableBuy)
  const [enableSell, setEnableSell] = useState(setting.enableSell)

  const [buyPriceType, setBuyPriceType] = useState<BuyPriceType>(setting.buyPriceType ?? "CurrentPrice")
  const [buyPriceValue, setBuyPriceValue] = useState(setting.buyPriceValue != null ? String(setting.buyPriceValue) : "")
  const [buyComparison, setBuyComparison] = useState<Comparison>(setting.buyComparison ?? "GreaterEqual")
  const [buyQtyMode, setBuyQtyMode] = useState<QtyMode>(setting.buyQtyMode ?? "amount")
  const [buyQtyValue, setBuyQtyValue] = useState(setting.buyQtyValue != null ? String(setting.buyQtyValue) : "")

  const [sellTargetMode, setSellTargetMode] = useState<SellTargetMode>(setting.sellTargetMode ?? "percent")
  const [sellTargetValue, setSellTargetValue] = useState(setting.sellTargetValue != null ? String(setting.sellTargetValue) : "")
  const [sellQtyMode, setSellQtyMode] = useState<SellQtyMode>(setting.sellQtyMode ?? "percent")
  const [sellQtyValue, setSellQtyValue] = useState(setting.sellQtyValue != null ? String(setting.sellQtyValue) : "")

  const isManual = strategyType === "Manual"
  const buyFieldsDisabled  = !isManual || !enableBuy
  const sellFieldsDisabled = !isManual || !enableSell

  const saveMutation = useMutation({
    mutationFn: () =>
      updateAutoTradingStockSettings(stockCode, {
        stockCode,
        isActive,
        accountNumber,
        strategyType,
        positionMode,
        positionValue: Number(positionValue),
        enableBuy,
        enableSell,
        buyPriceType: isManual && enableBuy ? buyPriceType : null,
        buyPriceValue: isManual && enableBuy ? Number(buyPriceValue) : null,
        buyComparison: isManual && enableBuy ? buyComparison : null,
        buyQtyMode: isManual && enableBuy ? buyQtyMode : null,
        buyQtyValue: isManual && enableBuy ? Number(buyQtyValue) : null,
        sellTargetMode: isManual && enableSell ? sellTargetMode : null,
        sellTargetValue: isManual && enableSell ? Number(sellTargetValue) : null,
        sellQtyMode: isManual && enableSell ? sellQtyMode : null,
        sellQtyValue: isManual && enableSell ? Number(sellQtyValue) : null,
      }),
    onSuccess: () => {
      toast.success("자동매매 설정이 저장되었습니다")
      queryClient.invalidateQueries({ queryKey: ["autotrading", "stocks"] })
      queryClient.invalidateQueries({ queryKey: ["autotrading", "stockSettings", stockCode] })
    },
    onError: () => toast.error("저장에 실패했습니다"),
  })

  const buyConditionValid =
    !isManual ||
    !enableBuy ||
    (buyPriceValue.trim() !== "" &&
      Number(buyPriceValue) > 0 &&
      buyQtyValue.trim() !== "" &&
      Number(buyQtyValue) > 0)

  const sellConditionValid =
    !isManual ||
    !enableSell ||
    (sellTargetValue.trim() !== "" &&
      Number(sellTargetValue) > 0 &&
      sellQtyValue.trim() !== "" &&
      Number(sellQtyValue) > 0 &&
      (sellQtyMode !== "percent" || Number(sellQtyValue) <= 100))

  const canSave =
    accountNumber !== "" &&
    positionValue.trim() !== "" &&
    Number(positionValue) > 0 &&
    buyConditionValid &&
    sellConditionValid

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-zinc-100">{stockName}</p>
          <p className="font-mono text-xs text-zinc-500">{stockCode}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-400">자동매매 활성화</span>
          <Switch checked={isActive} onCheckedChange={setIsActive} className="data-checked:bg-emerald-500" />
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-zinc-800 pt-4">
        <div className="flex items-center gap-5">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
            <Checkbox checked={enableBuy} onCheckedChange={(v) => setEnableBuy(v === true)} />
            매수 사용
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
            <Checkbox checked={enableSell} onCheckedChange={(v) => setEnableSell(v === true)} />
            매도 사용
          </label>
        </div>
        {!enableBuy && !enableSell && (
          <p className="text-xs text-orange-400">매수/매도가 모두 꺼져 있어 이 종목은 자동매매가 동작하지 않습니다.</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FieldRow label="계좌">
          <Select value={accountNumber} onValueChange={setAccountNumber}>
            <SelectTrigger className="w-full border-zinc-700 bg-zinc-950 text-zinc-100">
              <SelectValue placeholder="계좌 선택" />
            </SelectTrigger>
            <SelectContent className="border-zinc-700 bg-zinc-800">
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.accountNumber} className="text-zinc-100">
                  {a.accountName ? `${a.accountName} (${a.accountNumber})` : a.accountNumber}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="유형">
          <Select value={strategyType} onValueChange={(v) => setStrategyType(v as StrategyType)}>
            <SelectTrigger className="w-full border-zinc-700 bg-zinc-950 text-zinc-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-zinc-700 bg-zinc-800">
              {(Object.keys(STRATEGY_TYPE_LABELS) as StrategyType[]).map((key) => (
                <SelectItem key={key} value={key} className="text-zinc-100">
                  {STRATEGY_TYPE_LABELS[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldRow>
      </div>

      <div className="flex flex-col gap-2 border-t border-zinc-800 pt-4">
        <span className="text-sm text-zinc-400">포지션 방식</span>
        <div className="flex flex-wrap items-center gap-4">
          <RadioGroup
            value={positionMode}
            onValueChange={(v) => setPositionMode(v as PositionMode)}
            className="flex gap-6"
          >
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <RadioGroupItem value="amount" className={RADIO_CLASS} />
              금액 기준
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <RadioGroupItem value="quantity" className={RADIO_CLASS} />
              수량 기준
            </label>
          </RadioGroup>
          <Input
            type="number"
            min={0}
            value={positionValue}
            onChange={(e) => setPositionValue(e.target.value)}
            className={cn(INPUT_CLASS, "max-w-40")}
          />
          <span className="text-sm text-zinc-500">{positionMode === "amount" ? "원" : "주"}</span>
        </div>
      </div>

      {/* 매수 조건 (수동선택 + 매수 사용 시에만) */}
      <div className={cn("flex flex-col gap-2 border-t border-zinc-800 pt-4", buyFieldsDisabled && "opacity-50")}>
        <span className="text-sm text-zinc-400">
          매수 조건{!isManual ? " (수동선택 전용)" : !enableBuy ? " (매수 미사용)" : ""}
        </span>
        <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-300">
          <Select value={buyPriceType} onValueChange={(v) => setBuyPriceType(v as BuyPriceType)} disabled={buyFieldsDisabled}>
            <SelectTrigger className="w-24 border-zinc-700 bg-zinc-950 text-zinc-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-zinc-700 bg-zinc-800">
              {(Object.keys(BUY_PRICE_TYPE_LABELS) as BuyPriceType[]).map((key) => (
                <SelectItem key={key} value={key} className="text-zinc-100">
                  {BUY_PRICE_TYPE_LABELS[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>가</span>
          <Input
            type="number"
            min={0}
            disabled={buyFieldsDisabled}
            value={buyPriceValue}
            onChange={(e) => setBuyPriceValue(e.target.value)}
            className={cn(INPUT_CLASS, "w-28")}
          />
          <span>원</span>
          <Select value={buyComparison} onValueChange={(v) => setBuyComparison(v as Comparison)} disabled={buyFieldsDisabled}>
            <SelectTrigger className="w-20 border-zinc-700 bg-zinc-950 text-zinc-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-zinc-700 bg-zinc-800">
              {(Object.keys(COMPARISON_LABELS) as Comparison[]).map((key) => (
                <SelectItem key={key} value={key} className="text-zinc-100">
                  {COMPARISON_LABELS[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>일 때, 현금을 이용하여 시장가로</span>
          <Input
            type="number"
            min={0}
            disabled={buyFieldsDisabled}
            value={buyQtyValue}
            onChange={(e) => setBuyQtyValue(e.target.value)}
            className={cn(INPUT_CLASS, "w-24")}
          />
          <ToggleButtons
            value={buyQtyMode}
            onChange={setBuyQtyMode}
            disabled={buyFieldsDisabled}
            options={[
              { value: "amount", label: "원" },
              { value: "quantity", label: "주" },
            ]}
          />
          <span>매수</span>
        </div>
      </div>

      {/* 매도 조건 (수동선택 + 매도 사용 시에만) */}
      <div className={cn("flex flex-col gap-2 border-t border-zinc-800 pt-4", sellFieldsDisabled && "opacity-50")}>
        <span className="text-sm text-zinc-400">
          매도 조건{!isManual ? " (수동선택 전용)" : !enableSell ? " (매도 미사용)" : ""}
        </span>
        <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-300">
          <span>{sellTargetMode === "percent" ? "평단가 대비 +" : "목표가"}</span>
          <Input
            type="number"
            min={0}
            disabled={sellFieldsDisabled}
            value={sellTargetValue}
            onChange={(e) => setSellTargetValue(e.target.value)}
            className={cn(INPUT_CLASS, "w-24")}
          />
          <ToggleButtons
            value={sellTargetMode}
            onChange={setSellTargetMode}
            disabled={sellFieldsDisabled}
            options={[
              { value: "amount", label: "원" },
              { value: "percent", label: "%" },
            ]}
          />
          <span>도달 시</span>
          <Input
            type="number"
            min={0}
            max={sellQtyMode === "percent" ? 100 : undefined}
            disabled={sellFieldsDisabled}
            value={sellQtyValue}
            onChange={(e) => setSellQtyValue(e.target.value)}
            className={cn(INPUT_CLASS, "w-24")}
          />
          <ToggleButtons
            value={sellQtyMode}
            onChange={setSellQtyMode}
            disabled={sellFieldsDisabled}
            options={[
              { value: "amount", label: "원" },
              { value: "quantity", label: "주" },
              { value: "percent", label: "%" },
            ]}
          />
          <span>를 시장가에 매도</span>
        </div>
        {!sellFieldsDisabled && sellQtyMode !== "percent" && (
          <p className="text-xs text-zinc-500">
            {sellQtyMode === "quantity"
              ? "보유수량과 무관하게 정확히 이 수량만큼만 매도합니다(보유수량 초과 시 전량)."
              : "이 금액을 현재가로 나눈 수량만큼 매도합니다(보유수량 초과 시 전량)."}
          </p>
        )}
      </div>

      <SaveButton pending={saveMutation.isPending} disabled={!canSave} onClick={() => saveMutation.mutate()} />
    </div>
  )
}

function AutoTradingStockSettingPanel({ item }: { item: AutoTradingStockListItem }) {
  const { data: setting, isLoading: settingLoading } = useQuery({
    queryKey: ["autotrading", "stockSettings", item.stockCode],
    queryFn: () => getAutoTradingStockSettings(item.stockCode),
  })
  const { data: accounts = [], isLoading: accountsLoading } = useQuery({
    queryKey: ["portfolio", "accounts"],
    queryFn: getPortfolioAccounts,
  })

  if (settingLoading || accountsLoading || !setting) {
    return <CardSkeleton rows={5} title={item.stockName} />
  }

  return <AutoTradingStockSettingForm stockName={item.stockName} setting={setting} accounts={accounts} />
}

function AutoTradingStatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <Badge
      variant="outline"
      className={
        isActive
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
          : "border-zinc-500/40 bg-zinc-500/10 text-zinc-400"
      }
    >
      {isActive ? "활성" : "비활성"}
    </Badge>
  )
}

function BuySellStoppedBadge({ enableBuy, enableSell }: { enableBuy: boolean; enableSell: boolean }) {
  if (enableBuy || enableSell) return null
  return (
    <Badge variant="outline" className="border-orange-500/40 bg-orange-500/10 text-orange-400">
      매수/매도 중지
    </Badge>
  )
}

// 프론트 WatchlistStockTable(및 백엔드 WatchlistQueryService.NullLabel)과 동일한 널 값 필터 센티널
const NULL_LABEL = "(없음)"

export function AutoTradingStocksTab() {
  const [selected, setSelected] = useState<AutoTradingStockListItem | null>(null)

  // 이 탭은 자동매매 등록 종목만 다루는 소규모 목록이라 백엔드 페이지네이션 없이
  // 항상 전체를 불러오고, 컬럼 필터는 여기서 클라이언트 사이드로 직접 계산한다.
  const [colFilters, setColFilters] = useState<Record<ColKey, Set<string>>>({
    marketType:    new Set(),
    sector:        new Set(),
    conditionName: new Set(),
    state:         new Set(),
  })

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["autotrading", "stocks"],
    queryFn: getAutoTradingStocks,
  })

  const filterOptions: WatchlistFilterOptions = useMemo(() => ({
    marketTypes:    [...new Set(items.map((i) => i.marketType))].filter(Boolean).sort(),
    sectors:        [...new Set(items.map((i) => i.sector ?? NULL_LABEL))].sort(),
    conditionNames: [...new Set(items.map((i) => i.conditionName))].sort(),
    states:         [...new Set(items.map((i) => i.auditInfo ?? NULL_LABEL))].sort(),
  }), [items])

  const filteredItems = useMemo(() => items.filter((i) => {
    if (colFilters.marketType.size > 0 && !colFilters.marketType.has(i.marketType)) return false
    if (colFilters.sector.size > 0 && !colFilters.sector.has(i.sector ?? NULL_LABEL)) return false
    if (colFilters.conditionName.size > 0 && !colFilters.conditionName.has(i.conditionName)) return false
    if (colFilters.state.size > 0 && !colFilters.state.has(i.auditInfo ?? NULL_LABEL)) return false
    return true
  }), [items, colFilters])

  const handleFilterChange = (key: ColKey, val: Set<string>) =>
    setColFilters((prev) => ({ ...prev, [key]: val }))

  const handleSelect = (item: AutoTradingStockListItem) => setSelected(item)

  return (
    <div className="flex flex-col gap-3">
      {/* 가로 스크롤은 WatchlistStockTable 내부(shadcn Table)의 overflow-x-auto가 전담 —
          같은 요소에 overflow-x/y-auto를 함께 주면 iOS Safari에서 가로 스와이프가
          세로 스크롤에 먹혀 동작하지 않는 경우가 있어 이 래퍼는 세로만 담당한다 */}
      <div className="max-h-[480px] overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-900">
        <WatchlistStockTable
          items={filteredItems}
          isLoading={isLoading}
          selectedCode={selected?.stockCode}
          onSelect={handleSelect}
          renderAutoTradingColumn={(item) => (
            <div className="flex items-center gap-2">
              <AutoTradingStatusBadge isActive={item.isActive} />
              <BuySellStoppedBadge enableBuy={item.enableBuy} enableSell={item.enableSell} />
              <RemoveFromAutoTradingListButton stockCode={item.stockCode} stockName={item.stockName} />
            </div>
          )}
          filterOptions={filterOptions}
          colFilters={colFilters}
          onFilterChange={handleFilterChange}
        />
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
        {selected ? (
          <AutoTradingStockSettingPanel key={selected.stockCode} item={selected} />
        ) : (
          <div className="flex h-24 items-center justify-center text-sm text-zinc-500">
            종목을 선택하세요.
          </div>
        )}
      </div>
    </div>
  )
}
